/**
 * True when `path` is a safe, app-internal redirect target.
 *
 * A string-prefix heuristic (`startsWith('/') && !startsWith('//')`) is
 * insufficient: the browser normalizes `/\evil.com` (backslash) to the
 * protocol-relative `//evil.com` and follows it off-site, and percent-encoded
 * variants slip past prefix checks too. So we (a) require the INPUT itself to
 * be an absolute path (leading `/`) before parsing — a bare relative input like
 * `policy/123` would otherwise resolve onto the sentinel origin and falsely pass
 * — then (b) resolve the candidate against a fixed, non-routable sentinel origin
 * and require the result to stay on that origin. Anything that escapes the
 * origin (absolute URLs, protocol-relative `//evil.com`, normalized backslash
 * targets, `javascript:` schemes) resolves to a different origin and is
 * rejected. The leading-slash precheck replaces the (redundant) post-parse
 * `pathname.startsWith('/')` check. Dependency-free so it can be shared by
 * Login and MFAVerify.
 */
export const isSafeRedirectPath = (path: string): boolean => {
  if (typeof path !== 'string' || !path.startsWith('/')) {
    return false;
  }
  try {
    const url = new URL(path, 'https://internal.invalid');
    return url.origin === 'https://internal.invalid';
  } catch {
    return false;
  }
};
